import { errorResult, useStoreState } from 'pullstate'
import { AppStore } from '../stores/AppStore.js'
import axios from 'axios'
import { getTitles, getDepartments } from './getData.js'
import getBaseURL from './getBaseURL.js'

export const api = axios.create({
    baseURL: getBaseURL(),
})

api.interceptors.request.use(
    config => {
        const googleToken = AppStore.currentState.googleToken;

        if (googleToken) {
            config.headers.Authorization = `Bearer ${googleToken}`;
        }
        return config;
    },
    error => Promise.reject(error)
);

export const saveNewDepartmentAPI = async (newDepartment) => {
    try {
        const response = await api.post(`/departments`, { department: newDepartment })
        await getDepartments();
        return response.data
    } catch (error) {
        console.log(error)
        return error
    }
}

export const deleteGoalAPI = async (id) => {

    try {
        const response = await api.delete(`/goals/${id}`)
        return response.data
    } catch (error) {
        console.log(error)
        return { message: 'Something was wrong', type: 'error', error: error, status: 401 }
    }
}

export const createGoalAPI = async (goal) => {

    try {
        return await api.post('/goals', goal)
    } catch (error) {
        console.log(error)
        return { message: 'Something was wrong', type: 'error', error: error, status: 401 }
    }
}

export const updateGoalAPI = async (id, goal) => {

    try {
        return await api.post(`/goals/${id}`, goal)
    } catch (error) {
        console.log(error)
        return { message: 'Something was wrong', type: 'error', error: error, status: 401 }
    }
}

export const getSignature = () => `
    <div dir="ltr" class="gmail_signature" data-smartmail="gmail_signature">
        <div dir="ltr">
            <div dir="ltr">                
                <div dir="ltr">
                    <span class="im">
                        <div style="font-family:verdana,sans-serif;color:rgb(0,0,0)">
                            <font face="verdana, sans-serif" style="font-family:verdana,sans-serif;color:rgb(0,0,0)">
                                <b style="font-family:verdana,sans-serif">ETRO Lifecycle</b>
                            </font>                            
                        </div>
                        <br>
                        <div style="font-family:verdana,sans-serif;color:rgb(0,0,0)">
                            <font face="verdana, sans-serif" style="font-family:verdana,sans-serif;color:rgb(0,0,0)"><a href="https://www.google.com/maps/place/450+Nanaimo+St,+Vancouver,+BC+V5L+4W3/@49.280582,-123.0588871,17z/data=!3m1!4b1!4m6!3m5!1s0x548671227ace038d:0x5f7fa0f0530f1f60!8m2!3d49.2805785!4d-123.0563122!16s%2Fg%2F11rg66nx4w!5m1!1e1?authuser=0&amp;entry=ttu&amp;g_ep=EgoyMDI1MDkxMC4wIKXMDSoASAFQAw%3D%3D" style="font-family:verdana,sans-serif;color:black" target="_blank" data-saferedirecturl="https://www.google.com/url?q=https://www.google.com/maps/place/450%2BNanaimo%2BSt,%2BVancouver,%2BBC%2BV5L%2B4W3/@49.280582,-123.0588871,17z/data%3D!3m1!4b1!4m6!3m5!1s0x548671227ace038d:0x5f7fa0f0530f1f60!8m2!3d49.2805785!4d-123.0563122!16s%252Fg%252F11rg66nx4w!5m1!1e1?authuser%3D0%26entry%3Dttu%26g_ep%3DEgoyMDI1MDkxMC4wIKXMDSoASAFQAw%253D%253D&amp;source=gmail&amp;ust=1760129059088000&amp;usg=AOvVaw3gDM5ydlOqzvIGrbfjstP3">450
                                    Nanaimo Street, Vancouver, BC V5L 4W3</a></font>
                        </div>
                        <div style="font-family:verdana,sans-serif;color:rgb(0,0,0)">
                            <font face="verdana, sans-serif" style="font-family:verdana,sans-serif;color:rgb(0,0,0)"><a href="http://www.etroconstruction.com/" style="font-weight:bold;font-family:verdana,sans-serif;color:black" target="_blank" data-saferedirecturl="https://www.google.com/url?q=http://www.etroconstruction.com/&amp;source=gmail&amp;ust=1760129059088000&amp;usg=AOvVaw1zsA2Llv59VZ1pKcKyE0D9"><b style="font-family:verdana,sans-serif">ETRO
                                        Construction</b></a><br><br></font><br>
                            <font face="verdana, sans-serif" style="font-family:verdana,sans-serif;color:rgb(0,0,0)"></font>
                        </div>
                    </span>
                </div>
            </div>
        </div>
    </div>
`;

const getAppUrl = () => {
    switch ( process.env.REACT_APP_ENVIRONMENT ) {
        case 'dev':
            return 'https://dev-lifecycle.etroconstruction.dev'
        case 'staging':
            return 'https://staging-lifecycle.etroconstruction.dev'
        case 'prod':
            return 'https://lifecycle.etroconstruction.com'
    }
    throw new Error('Environment not specified.')
}

/**
 * 1. When all goals are set to ‘Active’: email to player
 * @function - Sends an email to a player requesting additional information for their goals.
 *
 * @param {Object} data - The data required to send the email.
 * @param {Object} data.player - The player to whom the email will be sent.
 * @param {number} data.year - The year for which the goals are active.
 * @returns {Promise<Object>} The response from the API call.
 * @throws Will log and return an error if the API call fails.
 */
export const sendEmailsAddittionalInfoToPlayer = async (data) => {
    let content = `
                <div>
                    <span>Hi ${data.player.full_name},</span>
                </div>
                <br>
                <div>
                    <span>Now that all of <b>your</b> goals for  ${data.year} are active, you will need to provide some additional information. </span>
                    <br>
                    <br>
                    <span> Please visit the People Builder portal in 
                    <a href="${getAppUrl()}/personal-people-builder/${data.player.id}">ETRO Lifecycle</a> at your earliest convenience to fill out the <b>Additional Information</b> form. </span>
                </div>
                <br>
                <br>
                <i> If you have any issues accessing the portal, please <a href="https://mail.google.com/mail/u/0/?fs=1&tf=cm&source=mailto&to=support@etroconstruction.zohodesk.com">contact IT</a>.
                </i>
                <br>
                <br>
                ${getSignature()}
                `
    const emailObject = {
        to: data.player.email,
        subject: `Additional information regarding your ${data.year} goals is requested`,
        content: content
    }

    try {
        return await api.post('/send-emails', emailObject)
    } catch (error) {
        console.log(error)
        return error
    }
}

/**
 * @function - 2. Sends an email to the coach requesting additional information regarding a player's goals.
 *
 * @param {Object} data - The data required to send the email.
 * @param {Object} data.coach - The coach's information.
 * @param {Object} data.player - The player's information.
 * @param {number} data.year - The year of the goals.
 * @returns {Promise<Object>} The response from the API or an error object.
 */
export const sendEmailsAddittionalInfoToCoach = async (data) => {
    const { employees } = AppStore.currentState;
    const coach = employees.find(employee => employee.full_name === data.player.coach);

    let content = `
                <div>
                    <span>Hi ${coach.full_name},</span>
                </div>
                <br>
                <div>
                    <span>Now that all of <b>${data.player.full_name}</b>’s goals for  ${data.year} are active, you will both need to provide some additional information. </span>
                    <br>
                    <br>
                    <span> Please visit the People Builder portal in 
                    <a href="${getAppUrl()}/team-people-builder/${coach.id}">ETRO Lifecycle</a> at your earliest convenience to fill out the <b>Additional Information</b> form. </span>
                </div>
                <br>
                <br>
                <i> If you have any issues accessing the portal, please <a href="https://mail.google.com/mail/u/0/?fs=1&tf=cm&source=mailto&to=support@etroconstruction.zohodesk.com">contact IT</a>.
                </i>
                <br>
                <br>
                ${getSignature()}

                `
    const emailObject = {
        to: coach.email,
        subject: `Additional information regarding ${coach.full_name}’s ${data.year} goals is requested`,
        content: content
    }

    try {
        return await api.post('/send-emails', emailObject)
    } catch (error) {
        console.log(error)
        return error
    }
}

export const sendEmailsGoalReadyForApproval = async (emailData) => {
    const { user } = emailData
    const { employees } = AppStore.currentState;
    const coach = employees.find(employee => employee.full_name === user.player.coach);

    let content = `
                <div>
                    <span>Hi ${coach.full_name} , </span>
                </div>
                <br>
                <div>
                    <span>Your player <b>${user.player.full_name}</b> has submitted the ${emailData.goal_term} goal <b>${emailData.goal_name}</b> for your approval! </span>
                    <br>
                    <br>
                    <span> Please visit the People Builder portal in 
                    <a href="${getAppUrl()}/team-people-builder/${coach.id}">ETRO Lifecycle</a> at your earliest convenience to evaluate this goal. </span>
                </div>
                <br>
                <br>
                <i> If you have any issues accessing the portal, please <a href="https://mail.google.com/mail/u/0/?fs=1&tf=cm&source=mailto&to=support@etroconstruction.zohodesk.com">contact IT</a>.
                </i>
                <br>
                <br>
                ${getSignature()}

                `
    const emailObject = {
        to: coach.email,
        subject: `${user.player.full_name}’s ${emailData.goal_term} goal is ready for review`,
        content: content
    }

    try {
        return await api.post('/send-emails', emailObject)
    } catch (error) {
        console.log(error)
        return error
    }
}

/**
 * @function - 3. Sends an email notification to a coach indicating that a player's goals are ready for approval.
 *
 * @param {Object} emailData - The data required to send the email.
 * @param {Object} emailData.coach - The coach's information.
 * @param {Object} emailData.player - The player's information.
 * @param {number} emailData.year - The year of the goals submission.
 * @returns {Promise<Object>} The response from the API call or an error object if the call fails.
 */
export const readyForApproval = async (emailData) => {
    const { employees } = AppStore.currentState;
    const coach = employees.find(employee => employee.full_name === emailData.player.coach);

    let content = `
                <div>
                    <span>Hi ${coach.full_name},</span>
                </div>
                <br>
                <div>
                    <span>Your player <b>${emailData.player.full_name}</b> has submitted the ${emailData.year} goals for your approval! </span>
                    <br>
                    <br>
                    <span> Please visit the People Builder portal in 
                    <a href="${getAppUrl()}/team-people-builder/${coach.id}">ETRO Lifecycle</a> at your earliest convenience to review these goals. </span>
                </div>
                <br>
                <br>
                <i> If you have any issues accessing the portal, please <a href="https://mail.google.com/mail/u/0/?fs=1&tf=cm&source=mailto&to=support@etroconstruction.zohodesk.com">contact IT</a>.
                </i>
                <br>
                <br>
                ${getSignature()}
                `
    const emailObject = {
        to: coach.email,
        subject: `${emailData.player.full_name}’s ${emailData.year} goals are ready for review`,
        content: content
    }

    try {
        return await api.post('/send-emails', emailObject)
    } catch (error) {
        console.log(error)
        return error
    }
}

/**
 * @function - 4. When the coach saves a goal with status  “Needs Revision.
 *
 * @param {Object} emailData - The data required to send the email.
 * @param {Object} emailData.coach - The coach's information.
 * @param {Object} emailData.player - The player's information.
 * @param {number} emailData.year - The year of the goals submission.
 * @returns {Promise<Object>} The response from the API call or an error object if the call fails.
 */
export const sendEmailsGoalNeedsRevision = async (emailData) => {
    const { user } = emailData
    const { employees } = AppStore.currentState;
    const coach = employees.find(employee => employee.full_name === user.player.coach);

    let content = `
                <div>
                    <span>Hi ${user.player.full_name},</span>
                </div>
                <br>
                <div>
                    <span>Your coach ${coach.full_name} has flagged the following goal(s) for revision:</span>
                    <br>
                    <br>
                    ${emailData.goal_term}: ${emailData.goal_name}
                    <br>
                    <br>
                    <span> Please visit the People Builder portal in 
                        <a href="${getAppUrl()}/personal-people-builder/${user.player.id}">ETRO Lifecycle</a> 
                        at your earliest convenience to see your coach's comments and revise these goals accordingly. 
                    </span>
                </div>
                <br>
                <br>
                <i> If you have any issues accessing the portal, please <a href="https://mail.google.com/mail/u/0/?fs=1&tf=cm&source=mailto&to=support@etroconstruction.zohodesk.com">contact IT</a>.
                </i>
                <br>
                <br>
                ${getSignature()}
                `
    const emailObject = {
        to: user.player.email,
        subject: `Goal ${emailData.goal_name} needs revision`,
        content: content
    }

    try {
        return await api.post('/send-emails', emailObject)
    } catch (error) {
        console.log(error)
        return error
    }
}

export const sendGenericEmailsGoalNeedsRevision = async (emailData) => {
    const { user } = emailData
    const { employees } = AppStore.currentState;
    const coach = employees.find(employee => employee.full_name === user.player.coach);

    let content = `
                <div>
                    <span>Hi ${user.player.full_name},</span>
                </div>
                <br>
                <div>
                    <span>Your coach ${coach.full_name} has flagged goal(s) for revision</span>
                    <br>
                    <br>
                    <span> Please visit the People Builder portal in 
                        <a href="${getAppUrl()}/personal-people-builder/${user.player.id}">ETRO Lifecycle</a> 
                        at your earliest convenience to see your coach's comments and revise these goals accordingly. 
                    </span>
                </div>
                <br>
                <br>
                <i> If you have any issues accessing the portal, please <a href="https://mail.google.com/mail/u/0/?fs=1&tf=cm&source=mailto&to=support@etroconstruction.zohodesk.com">contact IT</a>.
                </i>
                <br>
                <br>
                ${getSignature()}
                `
    const emailObject = {
        to: user.player.email,
        subject: `Goals need revision`,
        content: content
    }

    try {
        return await api.post('/send-emails', emailObject)
    } catch (error) {
        console.log(error)
        return error
    }
}

export const createTitle = async (newTitle) => {

    try {
        const res = await api.post(`/titles/`, { title: newTitle })
        getTitles();
        return res.data
    } catch (error) {
        console.log(error)
        return error
    }
}
